import { NotebookText, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import type { JournalEntry } from '../lib/types';
import { prettyDay } from '../ui/PlannerCalendar';
import { RichTextEditor, RichTextView } from '../ui/RichText';
import { Sheet } from '../ui/Sheet';
import { useParent } from './ParentContext';
import { WorkSamples } from './WorkSamples';

// Parent journal (#187): one rich-text entry per child per day, written from the planner calendar.

/** Plain-text preview of an entry, for the calendar chip and row. */
export const journalPreview = (html: string, max = 80) => {
  // A space after each paragraph, list item and line break, so blocks don't run together.
  const spaced = html.replace(/<(?:\/p|\/li|br)>/gi, '$& ');
  const text = new DOMParser().parseFromString(spaced, 'text/html').body.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

/** A journal entry's row on the calendar (day popup, week and day views). */
export function JournalRow({ entry, onOpen }: { entry: JournalEntry; onOpen: () => void }) {
  return (
    <div className="planner-item lesson-row">
      <button className="lesson-open" onClick={onOpen}>
        <NotebookText size={18} className="journal-icon" />
        <span className="grow">
          <strong>Journal</strong>
          <span className="muted small block">{journalPreview(entry.bodyHtml)}</span>
        </span>
      </button>
      <span className="row-gap planner-actions">
        <button className="btn small" onClick={onOpen}>
          Open
        </button>
      </span>
    </div>
  );
}

/** Read or write the journal entry for one day. Starts in edit mode when the day has no entry yet. */
export function JournalSheet({
  childId,
  childName,
  date,
  entry,
  onClose,
  onChanged,
}: {
  childId: string;
  childName: string;
  date: string;
  entry?: JournalEntry;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { toast, children } = useParent();
  const child = children.find((c) => c.id === childId);
  const [current, setCurrent] = useState(entry);
  const [editing, setEditing] = useState(!entry);
  const [html, setHtml] = useState(entry?.bodyHtml ?? '');
  const [busy, setBusy] = useState(false);

  async function save(body: string) {
    setBusy(true);
    try {
      const r = await api.put<JournalEntry | { deleted: true }>(`/api/children/${childId}/journal/${date}`, { html: body });
      onChanged();
      if ('deleted' in r) return onClose();
      setCurrent(r);
      setHtml(r.bodyHtml);
      setEditing(false);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete the journal entry for ${prettyDay(date)}?`)) return;
    await save('');
  }

  return (
    <Sheet title={`Journal — ${prettyDay(date)}`} onClose={onClose}>
      <div className="stack">
        {editing ? (
          <>
            <RichTextEditor
              label={`About ${childName}’s day`}
              value={html}
              onChange={setHtml}
              placeholder="How did the day go? Wins, struggles, things to remember…"
              minHeight={180}
            />
            <div className="row-gap">
              <button className="btn primary" disabled={busy || !journalPreview(html, 1)} onClick={() => save(html)}>
                Save entry
              </button>
              {current && (
                <button className="btn" disabled={busy} onClick={() => (setHtml(current.bodyHtml), setEditing(false))}>
                  Cancel
                </button>
              )}
            </div>
          </>
        ) : (
          current && (
            <>
              <div className="row-between">
                <span className="muted small">Written by {current.author || 'you'}</span>
                <span className="row-gap">
                  <button className="btn small" onClick={() => setEditing(true)}>
                    <Pencil size={14} /> Edit
                  </button>
                  <button className="btn small danger-text" onClick={remove} disabled={busy} aria-label="Delete journal entry" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </span>
              </div>
              <RichTextView html={current.bodyHtml} />
            </>
          )
        )}
        {child && <WorkSamples child={child} journalDate={date} />}
      </div>
    </Sheet>
  );
}
